import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginBodySchema, type LoginBody } from '@flixflow/shared';
import { useAuth } from '../contexts/AuthContext.js';
import { ApiError } from '../api/client.js';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginBody>({ resolver: zodResolver(LoginBodySchema) });

  async function onSubmit(data: LoginBody) {
    setServerError(null);
    try {
      await login(data);
      void navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setServerError('Benutzername oder Passwort falsch.');
      } else {
        setServerError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">FlixFlow Importer</h1>
          <p className="mt-1 text-sm text-neutral-500">Melden Sie sich an, um fortzufahren.</p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white border border-neutral-200 rounded-lg p-6 space-y-4 shadow-sm"
        >
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700">Benutzername</label>
            <input
              {...register('username')}
              autoComplete="username"
              autoFocus
              className="w-full rounded border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.username && (
              <p className="text-xs text-red-600">{errors.username.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-neutral-700">Passwort</label>
            <input
              {...register('password')}
              type="password"
              autoComplete="current-password"
              className="w-full rounded border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.password && (
              <p className="text-xs text-red-600">{errors.password.message}</p>
            )}
          </div>

          {serverError && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{serverError}</p>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white rounded py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? 'Anmelden …' : 'Anmelden'}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-neutral-400">
          Mittelstandsbüro AG · FlixFlow Importer
        </p>
      </div>
    </div>
  );
}
